import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { forkJoin } from 'rxjs';

import {
  FitDataService,
  OutlierCandidate,
  StreamCorrection,
  StreamCorrectionMode,
  StreamCorrectionsData,
} from '../../training-analysis/fit-data.service';

export interface PowerOutlierDialogData {
  sessionId: number | string;
  title: string;
  date?: string | null;
}

/** One detected range — either still open (decided=null) or decided. */
interface OutlierRow {
  start_s: number;
  end_s: number;
  peak_w: number | null;
  decided: StreamCorrectionMode | null;
  correctionId: number | null;
}

/**
 * Inspektions-/Korrektur-Dialog für Power-Ausreißer einer Session.
 *
 * Zeigt den Power-/Cadence-Verlauf (zoombar) mit hervorgehobenen
 * Verdachtsbereichen. Pro Bereich entscheidet der Nutzer bewusst: auf 0
 * setzen, interpolieren oder als korrekt bestätigen. Keine Automatik.
 */
@Component({
  selector: 'app-power-outlier-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
    NgxEchartsDirective,
  ],
  template: `
    <h2 mat-dialog-title>{{ 'calendar.outlier.dialog_title' | translate }}</h2>
    <mat-dialog-content class="po-content">
      <div class="po-head">
        <strong>{{ data.title }}</strong>
        @if (data.date) { <span class="po-date">{{ data.date }}</span> }
      </div>

      @if (loading) {
        <div class="po-loading"><mat-spinner diameter="28"></mat-spinner></div>
      } @else {
        @if (chartOption) {
          <div echarts [options]="chartOption" class="po-chart"
               (chartInit)="onChartInit($event)"></div>
          <p class="po-hint">{{ 'calendar.outlier.zoom_hint' | translate }}</p>
        }

        <h3 class="po-section">{{ 'calendar.outlier.detected' | translate }}</h3>
        @if (rows.length === 0) {
          <p class="po-empty">{{ 'calendar.outlier.none' | translate }}</p>
        } @else {
          @for (r of rows; track r.start_s) {
            <div class="po-range" [class.po-range--done]="r.decided">
              <div class="po-range__info">
                <span class="po-range__time">{{ fmt(r.start_s) }} – {{ fmt(r.end_s) }}</span>
                <span class="po-range__meta">
                  {{ (r.end_s - r.start_s + 1) }} s@if (r.peak_w != null) {
                    · {{ 'calendar.outlier.peak' | translate }} {{ r.peak_w | number:'1.0-0' }} W
                  }
                </span>
              </div>
              <div class="po-choices">
                @if (r.decided === 'zero') {
                  <button mat-flat-button color="primary" [disabled]="busy"
                          (click)="choose(r, 'zero')">
                    {{ 'calendar.outlier.set_zero' | translate }}
                  </button>
                } @else {
                  <button mat-stroked-button [disabled]="busy" (click)="choose(r, 'zero')">
                    {{ 'calendar.outlier.set_zero' | translate }}
                  </button>
                }
                @if (r.decided === 'interpolate') {
                  <button mat-flat-button color="primary" [disabled]="busy"
                          (click)="choose(r, 'interpolate')">
                    {{ 'calendar.outlier.interpolate' | translate }}
                  </button>
                } @else {
                  <button mat-stroked-button [disabled]="busy"
                          (click)="choose(r, 'interpolate')">
                    {{ 'calendar.outlier.interpolate' | translate }}
                  </button>
                }
                @if (r.decided === 'accept') {
                  <button mat-flat-button color="primary" [disabled]="busy"
                          (click)="choose(r, 'accept')">
                    {{ 'calendar.outlier.set_correct' | translate }}
                  </button>
                } @else {
                  <button mat-stroked-button [disabled]="busy"
                          (click)="choose(r, 'accept')">
                    {{ 'calendar.outlier.set_correct' | translate }}
                  </button>
                }
              </div>
            </div>
          }
        }

      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="{ changed: changed }">
        {{ 'calendar.close' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .po-content { min-width: 34rem; max-width: 92vw; }
    .po-head { display: flex; align-items: baseline; gap: 0.5rem; margin-bottom: 0.5rem; }
    .po-date { color: var(--text-secondary, #888); font-size: var(--fs-sm); }
    .po-loading, .po-busy { display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 0; }
    .po-busy { color: var(--text-secondary, #888); font-size: var(--fs-sm); }
    .po-chart { width: 100%; height: 320px; }
    .po-hint {
      margin: 0.1rem 0 0;
      font-size: var(--fs-xs);
      color: var(--text-secondary, #888);
    }
    .po-section { font-size: var(--fs-sm); margin: 0.9rem 0 0.35rem; }
    .po-empty { color: var(--text-secondary, #888); font-size: var(--fs-sm); }
    .po-range {
      display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
      padding: 0.4rem 0.55rem; margin-bottom: 0.35rem;
      border: 1px solid var(--border-color, #e0e0e0); border-radius: 6px;
    }
    .po-range--done { background: var(--surface-hover, #f5f5f5); }
    .po-range__info { flex: 1; min-width: 9rem; display: flex; flex-direction: column; }
    .po-range__time { font-weight: 600; }
    .po-range__meta { font-size: var(--fs-xs); color: var(--text-secondary, #888); }
    .po-choices { display: flex; gap: 0.4rem; flex-wrap: wrap; }
  `],
})
export class PowerOutlierDialogComponent implements OnInit, OnDestroy {
  private readonly fitData = inject(FitDataService);
  private readonly cdr = inject(ChangeDetectorRef);

  loading = true;
  busy = false;
  changed = false;

  rows: OutlierRow[] = [];
  chartOption: EChartsOption | null = null;

  private candidates: OutlierCandidate[] = [];
  private corrections: StreamCorrection[] = [];
  private power: (number | null)[] = [];
  private cadence: (number | null)[] = [];
  private chart: any = null;
  private brushTimer: any = null;
  private lastBrush: any = null;

  constructor(@Inject(MAT_DIALOG_DATA) public readonly data: PowerOutlierDialogData) {}

  ngOnInit(): void {
    forkJoin({
      fit: this.fitData.getFitData(this.data.sessionId),
      corr: this.fitData.getStreamCorrections(this.data.sessionId),
    }).subscribe({
      next: ({ fit, corr }) => {
        this.power = ((fit as any)?.power ?? []) as (number | null)[];
        this.cadence = ((fit as any)?.cadence ?? []) as (number | null)[];
        this.applyCorrData(corr);
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => { this.loading = false; this.cdr.markForCheck(); },
    });
  }

  ngOnDestroy(): void {
    if (this.brushTimer) {
      clearTimeout(this.brushTimer);
    }
    this.chart = null;
    // Batched background recompute: every decision made during this review
    // is folded into a single MMP/summary recompute when the dialog closes.
    if (this.changed) {
      this.fitData.recomputeStreams(this.data.sessionId)
        .subscribe({ next: () => {}, error: () => {} });
    }
  }

  private applyCorrData(corr: { outlier_candidates?: OutlierCandidate[];
                                corrections?: StreamCorrection[] } | null): void {
    this.candidates = corr?.outlier_candidates ?? [];
    this.corrections = corr?.corrections ?? [];
    this.buildRows();
    this.chartOption = this.buildChart();
    // Rebuilt options re-create the brush component — re-arm the cursor.
    setTimeout(() => this.activateBrush(), 0);
  }

  /** Combine open candidates + decided corrections into one ordered list.
   *  A correction overlapping a candidate range supersedes that candidate
   *  (the candidate row stays stale until the deferred recompute). */
  private buildRows(): void {
    const rows: OutlierRow[] = [];
    for (const c of this.corrections) {
      rows.push({ start_s: c.start_s, end_s: c.end_s, peak_w: null,
                  decided: c.mode, correctionId: c.id });
    }
    for (const cand of this.candidates) {
      const covered = this.corrections.some(
        (co) => co.start_s <= cand.end_s && cand.start_s <= co.end_s);
      if (!covered) {
        rows.push({ start_s: cand.start_s, end_s: cand.end_s, peak_w: cand.peak_w,
                    decided: null, correctionId: null });
      }
    }
    rows.sort((a, b) => a.start_s - b.start_s);
    this.rows = rows;
  }

  /** Apply a decision to a range. Clicking the active choice again clears it.
   *  Fast: only records the decision — the recompute is deferred to close. */
  choose(row: OutlierRow, mode: StreamCorrectionMode): void {
    if (this.busy) {
      return;
    }
    this.busy = true;
    this.cdr.markForCheck();
    const after = (data: StreamCorrectionsData | null) => {
      this.changed = true;
      this.applyCorrData(data);
      this.busy = false;
      this.cdr.markForCheck();
    };
    const fail = () => { this.busy = false; this.cdr.markForCheck(); };

    if (row.decided === mode && row.correctionId != null) {
      this.fitData.deleteStreamCorrection(this.data.sessionId, row.correctionId)
        .subscribe({ next: after, error: fail });
    } else {
      this.fitData.addStreamCorrection(this.data.sessionId, {
        start_s: row.start_s, end_s: row.end_s, mode,
      }).subscribe({ next: after, error: fail });
    }
  }

  fmt(seconds: number): string {
    const s = Math.max(0, Math.round(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(sec).padStart(2, '0');
    return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
  }

  // ── drag-to-zoom (same gesture as the main training-analysis chart) ──

  onChartInit(chart: any): void {
    this.chart = chart;
    chart.on('brushSelected', (p: any) => this.onBrushSelected(p));
    // ngx-echarts applies [options] (incl. the brush component) right AFTER
    // emitting chartInit — defer so takeGlobalCursor actually finds it.
    setTimeout(() => this.activateBrush(), 0);
  }

  private activateBrush(): void {
    this.chart?.dispatchAction({
      type: 'takeGlobalCursor',
      key: 'brush',
      brushOption: { brushType: 'lineX', brushMode: 'single' },
    });
  }

  // brushSelected fires continuously while dragging — debounce and process
  // the final selection once (a re-entrant dispatchAction freezes the brush).
  private onBrushSelected(params: any): void {
    this.lastBrush = params;
    if (this.brushTimer) {
      clearTimeout(this.brushTimer);
    }
    this.brushTimer = setTimeout(() => {
      this.brushTimer = null;
      const p = this.lastBrush;
      this.lastBrush = null;
      this.processBrush(p);
    }, 200);
  }

  private processBrush(params: any): void {
    if (!this.chart) {
      return;
    }
    const range = params?.batch?.[0]?.areas?.[0]?.coordRange;
    this.chart.dispatchAction({ type: 'brush', areas: [] });
    if (!range) {
      return;
    }
    const n = this.power.length - 1;
    if (n <= 0) {
      return;
    }
    const a = Math.max(0, Math.min(n, Math.round(Number(range[0]))));
    const b = Math.max(0, Math.min(n, Math.round(Number(range[1]))));
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    if (hi <= lo) {
      return;   // click, not a drag
    }
    this.chart.dispatchAction({ type: 'dataZoom', startValue: lo, endValue: hi });
    this.activateBrush();   // re-arm for the next drag
  }

  private buildChart(): EChartsOption | null {
    if (!this.power.length) {
      return null;
    }
    const markArea: any[] = [];
    const markPoint: any[] = [];
    for (const r of this.rows) {
      let color: string;
      if (r.decided === null) {
        color = 'rgba(229, 57, 53, 0.28)';      // open candidate — red
      } else if (r.decided === 'accept') {
        color = 'rgba(120, 144, 156, 0.28)';    // confirmed correct — grey
      } else {
        color = 'rgba(67, 160, 71, 0.28)';      // zero / interpolate — green
      }
      markArea.push([
        { xAxis: r.start_s, itemStyle: { color } },
        { xAxis: r.end_s },
      ]);
      if (r.decided === null) {
        const mid = Math.round((r.start_s + r.end_s) / 2);
        markPoint.push({
          coord: [mid, r.peak_w ?? this.power[mid] ?? 0],
          value: Math.round(r.peak_w ?? 0),
          itemStyle: { color: '#e53935' },
        });
      }
    }

    return {
      grid: { left: 52, right: 52, top: 40, bottom: 64 },
      tooltip: { trigger: 'axis' },
      legend: { data: ['Power', 'Cadence'], top: 6, left: 'center' },
      // Empty-type brush component + restore button. A drag in the chart
      // draws a lineX selection (see activateBrush) which processBrush turns
      // into a horizontal zoom — same gesture as the main analysis chart.
      toolbox: { top: 2, right: 8, feature: { restore: {}, brush: { type: [] } } },
      brush: { toolbox: [], brushMode: 'single', xAxisIndex: 'all' },
      xAxis: {
        type: 'category',
        data: this.power.map((_, i) => i),
        boundaryGap: false,
        axisLabel: { formatter: (v: string) => this.fmt(Number(v)) },
      },
      yAxis: [
        { type: 'value', name: 'W' },
        { type: 'value', name: 'rpm', position: 'right', splitLine: { show: false } },
      ],
      dataZoom: [
        // Drag zooms (via the brush); the mouse wheel pans horizontally.
        { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: false,
          moveOnMouseMove: false, moveOnMouseWheel: true },
        {
          type: 'slider', xAxisIndex: 0, bottom: 8, height: 18,
          labelFormatter: (v: number) => this.fmt(v),
        },
      ],
      series: [
        {
          name: 'Power', type: 'line', yAxisIndex: 0,
          data: this.power as number[],
          showSymbol: false,
          lineStyle: { width: 1, color: '#43a047' },
          markArea: { silent: true, data: markArea },
          markPoint: {
            symbol: 'pin', symbolSize: 38,
            label: { fontSize: 9, color: '#fff' },
            data: markPoint,
          },
        },
        {
          name: 'Cadence', type: 'line', yAxisIndex: 1,
          data: this.cadence as number[],
          showSymbol: false,
          lineStyle: { width: 1, color: '#1e88e5', opacity: 0.65 },
        },
      ],
    };
  }
}
